import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Tab from '@mui/material/Tab'
import Chip from '@mui/material/Chip'
import Tabs from '@mui/material/Tabs'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableContainer from '@mui/material/TableContainer'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'

import { useEffect, useState } from "react";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { AccountBalance, AdminPanelSettings, AssignmentInd, Business, CalendarToday, CheckCircle, Dashboard, Edit, Email, History, LocationOn, Lock, Person, Phone, TrendingUp, WorkspacePremium } from "@mui/icons-material";
import { Avatar, Badge, LinearProgress, List, ListItem, ListItemIcon, ListItemText, styled } from '@mui/material'
import UserFormDrawer from './UserFormDrawer'

const UserDetailPage = () => {
  const [elevation, setElevation] = useState(1);
  const [tabIndex, setTabIndex] = useState(0);
  const [userData, setUserData] = useState<any>(null);
  const [selectedIdForEdit, setSelectedIdForEdit] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { id } = useParams();
  const { showMessage } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await callAPI(`users/detail/${id}`, Method.GET);
        
        setUserData(res);
      } catch (error: any) {
        console.error("Failed to fetch data:", error);
        if (error.message === 'GlobalHandledError') return;
        showMessage(
          error.response?.data.message || "Failed to load user data",
          "error"
        );
      }
    };

    fetchData();
  }, [id]);

  if (!userData || !userData.user)
    return (
      <Box height="80vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress sx={{ color: "#4AD06E" }} />
      </Box>
    );

  const { user, loans = [], totalLoans = 0 } = userData;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Sample activity data
  const activities = [
    { id: 1, action: 'Logged in', timestamp: new Date(Date.now() - 1000 * 60 * 30) },
    { id: 2, action: 'Updated profile', timestamp: new Date(Date.now() - 1000 * 60 * 120) },
    { id: 3, action: 'Created new loan application', timestamp: new Date(Date.now() - 1000 * 60 * 360) },
  ];

  const handleEdit = (id: string) => {
    setSelectedIdForEdit(id);
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedIdForEdit(null)
  }

  return (
    <Box p={3} mb={5}>
      {/* User Header */}
      <Grid container spacing={3}>
        {/* Left Panel */}
        <Grid item xs={12} md={4}>
          <GradientHeader>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                User Profile
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                Complete user information and details
              </Typography>
            </Box>
            {/* <ProfileAvatar>
              <AccountCircle fontSize="inherit" />
            </ProfileAvatar> */}
          </GradientHeader>

          {/* User Card */}
          <PremiumCard
            sx={{ mt: 7 }}
            onMouseEnter={() => setElevation(4)}
            onMouseLeave={() => setElevation(1)}
            elevation={elevation}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Badge color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Basic Information
                </Typography>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              <DetailItem>
                <Person color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {user.name}
                  </Typography>
                </Box>
              </DetailItem>

              <DetailItem>
                <AssignmentInd color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {user.role}
                  </Typography>
                </Box>
              </DetailItem>

              <DetailItem>
                <WorkspacePremium color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <StatusBadge status={user.isActive ? "Active" : "Inactive"}>
                    {user.isActive ? "Active" : "Inactive"}
                  </StatusBadge>
                </Box>
              </DetailItem>

              <Tooltip title="Edit User Profile">
                <EditButton
                  fullWidth
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => handleEdit(user._id)}
                  sx={{ mt: 2.5 }}
                >
                  Edit Profile
                </EditButton>
              </Tooltip>
            </CardContent>
          </PremiumCard>
        </Grid>

        {/* Right Panel */}
        <Grid item xs={12} md={8}>
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Contact Information
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <DetailItem>
                    <Email color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        component={user.email ? 'a' : 'span'}
                        href={user.email ? `mailto:${user.email}` : undefined}
                        sx={{
                          textDecoration: user.email ? 'none' : undefined,
                          color: user.email ? 'inherit' : 'text.disabled',
                          cursor: user.email ? 'pointer' : 'default',
                          '&:hover': user.email ? { textDecoration: 'underline' } : undefined,
                        }}
                      >
                        {user.email || "N/A"}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem>
                    <Phone color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Mobile Number
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        component="a"
                        href={`tel:${user.mobile}`}
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {user.mobile}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                {user.district && (
                  <Grid item xs={12} sm={4}>
                    <DetailItem>
                      <LocationOn color="action" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          District
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {user.district.name}
                        </Typography>
                      </Box>
                    </DetailItem>
                  </Grid>
                )}

                {user.block && (
                  <Grid item xs={12} sm={4}>
                    <DetailItem>
                      <LocationOn color="action" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Block
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {user.block.name}
                        </Typography>
                      </Box>
                    </DetailItem>
                  </Grid>
                )}

                {user.cds && (
                  <Grid item xs={12} sm={4}>
                    <DetailItem>
                      <LocationOn color="action" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          CDS
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {user.cds.name}
                        </Typography>
                      </Box>
                    </DetailItem>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </PremiumCard>

          {/* Additional Information Card (Optional) */}
          <PremiumCard sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Additional Information
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <DetailItem>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Member Since
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        January 15, 2023
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        March 22, 2024
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>
              </Grid>
            </CardContent>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Divider sx={{ my: 3 }} />

      <PremiumTabs
        value={tabIndex}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <PremiumTab icon={<Dashboard />} iconPosition="start" label="Overview" />
        <PremiumTab icon={<AccountBalance />} iconPosition="start" label={`Loans (${totalLoans})`} />
        <PremiumTab icon={<TrendingUp />} iconPosition="start" label="Features" />
        <PremiumTab icon={<History />} iconPosition="start" label="Activity" />
        <PremiumTab icon={<Lock />} iconPosition="start" label="Permissions" />
      </PremiumTabs>

      {/* Tab Panels */}
      <Box>
        {/* Overview Tab */}
        {tabIndex === 0 && (
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Dashboard color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  User Overview
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <DetailItem>
                    <Person color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Full Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user.name}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailItem>
                    <Email color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user.email || "N/A"}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailItem>
                    <Phone color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Mobile Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user.mobile}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailItem>
                    <AdminPanelSettings color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Role
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user.role}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>

                <Grid item xs={12}>
                  <DetailItem>
                    <CalendarToday color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Member Since
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(user.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </DetailItem>
                </Grid>
              </Grid>
            </CardContent>
          </PremiumCard>
        )}

        {/* Loans Tab */}
        {tabIndex === 1 && (
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Loans Managed ({loans.length})
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {loans.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AccountBalance sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    No loans found for this user.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table size="small" aria-label="loans table">
                    <TableHead sx={{ bgcolor: 'primary.light' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Loan ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Enterprise</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Mobile</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Amount (₹)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Created At</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Last Updated At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loans.map((loan: any) => (
                        <StyledTableRow key={loan._id} hover>
                          <TableCell sx={{ fontWeight: 'medium' }}>{loan.loanId}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Business sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
                              {loan.enterpriseName}
                            </Box>
                          </TableCell>
                          <TableCell>{loan.mobile}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>₹{loan.appliedAmount}</TableCell>
                          <TableCell>
                            <StatusChip
                              label={loan.status.toUpperCase()}
                              status={loan.status}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(loan.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {new Date(loan.updatedAt).toLocaleDateString()}
                          </TableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </PremiumCard>
        )}

        {/* Features Tab */}
        {tabIndex === 2 && (
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Features Accessed
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <List>
                {user.role === "admin" && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Full Administrative Access"
                        secondary="Complete system control and management"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="User Management"
                        secondary="Create, edit, and manage all users"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Data Analytics"
                        secondary="Access to all reports and analytics"
                      />
                    </ListItem>
                  </>
                )}

                {user.role === "district" && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="District-level Management"
                        secondary="Oversee all blocks and CDS in the district"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Performance Reports"
                        secondary="Access to district-level analytics"
                      />
                    </ListItem>
                  </>
                )}

                {user.role === "block" && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Block-level Management"
                      secondary="Manage CDS and loan applications in the block"
                    />
                  </ListItem>
                )}

                {user.role === "cds" && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Loan Creation and Approvals"
                      secondary="Create and review loan applications"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </PremiumCard>
        )}

        {/* Activity Tab */}
        {tabIndex === 3 && (
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <History color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Recent Activity
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {activities.length > 0 ? (
                <List>
                  {activities.map((activity) => (
                    <ListItem key={activity.id} divider>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <History sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.action}
                        secondary={activity.timestamp.toLocaleString()}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    No recent activity found.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </PremiumCard>
        )}

        {/* Permissions Tab */}
        {tabIndex === 4 && (
          <PremiumCard>
            <CardContent sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Lock color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6" fontWeight="600">
                  Access Permissions
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ width: '100%', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Permission Level
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {user.role.toUpperCase()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={user.role === "admin" ? 100 : user.role === "district" ? 75 : user.role === "block" ? 50 : 25}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)',
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>

              <Typography variant="body1" paragraph>
                {user.role === "admin"
                  ? "You have full administrative access to all system features and data."
                  : `You have access to ${user.role} level features and data management.`}
              </Typography>

              {user.role !== "admin" && (
                <Typography variant="body2" color="text.secondary">
                  Contact an administrator if you need elevated permissions.
                </Typography>
              )}
            </CardContent>
          </PremiumCard>
        )}
      </Box>

      {/* {tabIndex === 5 && (
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                CDS & Block Access
              </Typography>

              {(user.role === "admin" || user.role === "district" || user.role === "block") ? (
                <Box mt={2}>
                  {(user.role === "admin" || user.role === "district" || user.role === "block") && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        CDS List
                      </Typography>
                      {cdsList.length > 0 ? (
                        <List dense disablePadding sx={{ mb: 2 }}>
                          {cdsList.map((cds: any, index: number) => (
                            <Box key={cds._id}>
                              <ListItem sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={`${cds.name} (${cds.code})`}
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>
                              {index !== cdsList.length - 1 && <Divider />}
                            </Box>
                          ))}
                        </List>
                      ) : (
                        <Typography color="text.secondary">No CDS available</Typography>
                      )}
                    </>
                  )}

                  {(user.role === "admin" || user.role === "district") && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Block List
                      </Typography>
                      {blockList.length > 0 ? (
                        <List dense disablePadding>
                          {blockList.map((block: any, index: number) => (
                            <Box key={block._id}>
                              <ListItem sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={`${block.name} (${block.code})`}
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>
                              {index !== blockList.length - 1 && <Divider />}
                            </Box>
                          ))}
                        </List>
                      ) : (
                        <Typography color="text.secondary">No Blocks available</Typography>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  No CDS or Block access
                </Typography>
              )}
            </CardContent>
          </Card>
        )} */}

      <UserFormDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        id={selectedIdForEdit}
      />
    </Box>
  );
};

export default UserDetailPage;


// Styled components for premium look
const GradientHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4DD26B 0%, #6A65B8 100%)',
  padding: theme.spacing(3),
  borderBottomRightRadius: '40px',
  borderTopLeftRadius: '12px',
  height: '11rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  boxShadow: '0 8px 32px rgba(77, 210, 107, 0.3)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 60%)',
    opacity: '0.4',
  }
}));

// const ProfileAvatar = styled(Avatar)(({ theme }) => ({
//   width: 100,
//   height: 100,
//   backgroundColor: '#6A65B8',
//   boxShadow: '0 8px 24px rgba(106, 101, 184, 0.4)',
//   border: '4px solid white',
//   position: 'absolute',
//   bottom: '-40px',
//   right: '20px',
//   fontSize: '3.5rem',
//   transition: 'all 0.3s ease',
//   '&:hover': {
//     transform: 'scale(1.05)',
//     boxShadow: '0 12px 32px rgba(106, 101, 184, 0.5)',
//   }
// }));

const PremiumCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
  }
}));

const DetailItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: '12px',
  backgroundColor: 'rgba(245, 245, 245, 0.5)',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(77, 210, 107, 0.08)',
    transform: 'translateX(4px)',
  }
}));

const EditButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)',
  color: 'white',
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: '0 6px 16px rgba(74, 209, 110, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 20px rgba(74, 209, 110, 0.4)',
    transform: 'translateY(-2px)',
  }
}));


interface StatusBadgeProps extends BoxProps {
  status: 'Active' | 'Inactive';
}

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status' // this ensures 'status' is not passed to the DOM
})<StatusBadgeProps>(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: status === 'Active' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
  color: status === 'Active' ? '#2e7d32' : '#d32f2f',
}));


// Styled components for premium look
const PremiumTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '60px',
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    height: '4px',
    borderRadius: '2px',
    background: 'linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)',
  },
  '& .MuiTabs-scrollButtons': {
    color: theme.palette.primary.main,
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
}));

const PremiumTab = styled(Tab)(({ theme }) => ({
  minHeight: '60px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(74, 209, 110, 0.08)',
  },
  transition: 'all 0.2s ease',
}));


const StatusChip = styled(Chip)(({ status, theme }: { status: string, theme?: any }) => {
  let color, backgroundColor;

  switch (status) {
    case 'pending':
      color = theme.palette.warning.main;
      backgroundColor = theme.palette.warning.light;
      break;
    case 'rejected':
      color = theme.palette.error.main;
      backgroundColor = theme.palette.error.light;
      break;
    case 'sanctioned':
      color = theme.palette.success.main;
      backgroundColor = theme.palette.success.light;
      break;
    default:
      color = theme.palette.grey[600];
      backgroundColor = theme.palette.grey[200];
  }

  return {
    backgroundColor,
    color,
    fontWeight: 600,
    borderRadius: '12px',
    minWidth: '80px',
  };
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'rgba(74, 209, 110, 0.05)',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));